import { DOCUMENT } from '@angular/common';
import {
	ApplicationRef,
	computed,
	createComponent,
	effect,
	EmbeddedViewRef,
	inject,
	Injectable,
	Renderer2,
	RendererFactory2,
	Type,
	untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { ReachIntegration } from '@reach/interfaces/integrations/integration-type.enum';
import { QuotaStatusResponse } from '@reach/interfaces/quotas/quota.responses';
import { QuotaTypeEnum, StandardQuotaTypeEnum } from '@reach/interfaces/quotas/quota-type.enum';

import { QuotaApiService } from '~app-client/api/services';
import { FileSizeConfig } from '~app-client/core/utils';
import {
	ChatAiConversationsExceededComponent,
	ChatAiExceededComponent,
	NodesExceededComponent,
	StorageExceededComponent,
} from '~app-client/quota/components';
import { QuotaService as CommonQuotaService } from '~ng-common-lib/quota/services/quota.service';

import { SessionService } from '../session/session.service';
import { ReachQuotaStatus } from './quota.type';
import { commonQuotaTransformer, parseResponse, quotaCalculations } from './quota.utils';

export const CONTACT_US_URL = 'mailto:contact@rea.ch';

/**
 * Service that handles all the logic regarding quota.
 */
@Injectable({ providedIn: 'root' })
export class QuotaService {
	private readonly quotaApiService = inject(QuotaApiService);
	private readonly commonQuotaService = inject(CommonQuotaService);

	private readonly quotas$$ = new BehaviorSubject<ReachQuotaStatus | null>(null);
	public readonly quotas$ = this.quotas$$.asObservable();
	public readonly $quota = toSignal(this.quotas$$.asObservable());

	public readonly $standarQuota = computed(() => {
		const quota = this.$quota();
		if (!quota) {
			return null;
		}
		const { [QuotaTypeEnum.INTEGRATION_NODES]: _integrationNodes, ...standardQuotas } = quota;
		return standardQuotas;
	});

	public readonly $integrationQuota = computed(() => {
		const quota = this.$quota();
		if (!quota) {
			return null;
		}
		return quota[QuotaTypeEnum.INTEGRATION_NODES];
	});

	private readonly $hasQuota = computed(() => {
		const standardQuota = this.$standarQuota();
		const integrationQuota = this.$integrationQuota();
		return (
			standardQuota &&
			integrationQuota &&
			Object.keys(standardQuota).length > 0 &&
			Object.keys(integrationQuota).length > 0
		);
	});

	private readonly sessionService = inject(SessionService);
	private readonly document: Document = inject(DOCUMENT);
	private readonly rendererFactory = inject(RendererFactory2);
	private readonly appRef = inject(ApplicationRef);
	private readonly renderer: Renderer2;

	public static readonly fileSizeConfig: FileSizeConfig = {
		spacing: true,
		multiplier: 1000,
		precision: 2,
	};

	private readonly loading$$ = new BehaviorSubject(false);
	public readonly loading$ = this.loading$$.asObservable();

	private reqId = 0;

	constructor() {
		this.renderer = this.rendererFactory.createRenderer(null, null);
		this.sessionService.user$
			.pipe(
				map((user) => {
					return user?.id;
				}),
				distinctUntilChanged(),
				takeUntilDestroyed()
			)
			.subscribe((userId) => {
				if (!userId) {
					this.reset();
				} else {
					this.load(true);
				}
			});

		effect(() => {
			const quota = this.$quota();

			untracked(() => {
				this.commonQuotaService.setAllQuotas(commonQuotaTransformer(quota ?? null));
			});
		});
	}

	public async showNodesQuotaExceeded(): Promise<void> {
		this.loadComponent(NodesExceededComponent);
	}

	public async showStorageQuotaExceeded(): Promise<void> {
		this.loadComponent(StorageExceededComponent);
	}

	public async showChatAiQuotaExceeded(): Promise<void> {
		this.loadComponent(ChatAiExceededComponent);
	}

	public async showChatAiConversationsQuotaExceeded(): Promise<void> {
		this.loadComponent(ChatAiConversationsExceededComponent);
	}

	private async loadComponent<T>(component: Type<T>): Promise<void> {
		const componentRef = createComponent(component, {
			environmentInjector: this.appRef.injector,
		});
		this.appRef.attachView(componentRef.hostView);
		const domElement = (componentRef.hostView as EmbeddedViewRef<HTMLElement>)
			.rootNodes[0] as HTMLElement;
		this.renderer.appendChild(this.document.body, domElement);
	}

	public isIntegrationQuotaLimitReached(integration: ReachIntegration): boolean {
		const quota = this.$quota()?.[QuotaTypeEnum.INTEGRATION_NODES][integration];
		if (!quota) {
			return false;
		}
		return quota.used === quota.total;
	}

	public editUsedQuota(type: StandardQuotaTypeEnum, amount: number): void {
		const prevQuota = this.$quota();

		if (!prevQuota) {
			return;
		}

		const prev = prevQuota[type];

		if (!prev) {
			return;
		}

		const used = Math.max(0, Math.min(prev.total, prev.used + amount));

		const { percentage, showWarning } = quotaCalculations({
			...prev,
			used,
		});

		this.setQuota({
			...prevQuota,
			[type]: {
				...prev,
				used,
				percentage,
				showWarning,
			},
		} as QuotaStatusResponse);
	}

	public setQuota(quota: QuotaStatusResponse): void {
		const prevQuota = this.$quota();
		if (!prevQuota) {
			return;
		}
		const newQuota = {
			...prevQuota,
			...quota,
			[QuotaTypeEnum.INTEGRATION_NODES]: {
				...prevQuota[QuotaTypeEnum.INTEGRATION_NODES],
				...(quota[QuotaTypeEnum.INTEGRATION_NODES] ?? {}),
			},
		};
		this.quotas$$.next(parseResponse(newQuota as QuotaStatusResponse));
	}

	public async load(force = false): Promise<void> {
		if (!force && this.$hasQuota()) {
			return;
		}

		const reqId = ++this.reqId;

		try {
			this.loading$$.next(true);
			this.quotaApiService.cleanCache();
			const response = await this.quotaApiService.getUserQuota();

			if (this.ifValid(reqId)) {
				this.quotas$$.next(parseResponse(response));
			}
		} catch (error) {
			console.error(error);
		} finally {
			if (this.ifValid(reqId)) {
				this.loading$$.next(false);
			}
		}
	}

	public reset(): void {
		++this.reqId;
		this.quotas$$.next(null);
		this.loading$$.next(false);
	}

	private ifValid(reqId: number): boolean {
		return reqId === this.reqId;
	}
}
